import * as Sentry from "@sentry/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UserProfile } from "@shared/models/auth";

async function fetchUser(): Promise<UserProfile | null> {
  const response = await fetch("/api/auth/user", {
    credentials: "include",
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  return response.json();
}

async function logout(): Promise<void> {
  localStorage.removeItem("tpt_referral_code");
  localStorage.removeItem("tpt_referrer_company");
  sessionStorage.removeItem("tpt_banner_dismissed");
  window.location.href = "/api/logout";
}

export function useAuth() {
  const queryClient = useQueryClient();
  const { data: user, isLoading } = useQuery<UserProfile | null>({
    queryKey: ["/api/auth/user"],
    queryFn: async () => {
      const result = await fetchUser();
      if (result?.id) {
        Sentry.setUser({ id: result.id, email: result.email ?? undefined });
      } else {
        Sentry.setUser(null);
      }
      return result;
    },
    retry: false,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      Sentry.setUser(null);
      queryClient.setQueryData(["/api/auth/user"], null);
    },
  });

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  };
}
